<script setup lang="ts">
/**
 * /check — free, public coordinate check.
 *
 * Two states on one route:
 *   - no lat/lng query: empty form for paste-and-go
 *   - lat/lng present : result page (shareable URL)
 *
 * Form is intentionally a bare <div> (not <form>) so the browser
 * never falls back to a native GET submit that would strip the
 * canonical ?lat=&lng= query — submission goes via @click on the
 * button and @keyup.enter on the input.
 */
import type { CheckResult } from '~/types/check'
import type { HorizonProfileData } from '~/types/horizon'
import { formatLatLng } from '~/utils/eclipse'
import { parseCoordinates } from '~/utils/parseCoordinates'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const config = useRuntimeConfig()
const siteUrl = (config.public.siteUrl as string) || 'https://eclipsechase.is'

const input = ref('')
const error = ref('')

const latParam = computed(() => {
  const v = route.query.lat
  return typeof v === 'string' ? v : ''
})
const lngParam = computed(() => {
  const v = route.query.lng
  return typeof v === 'string' ? v : ''
})
const hasQueryCoords = computed(() => latParam.value !== '' && lngParam.value !== '')

const { data: result, pending, error: fetchError } = await useFetch<CheckResult>(
  '/api/check',
  {
    query: { lat: latParam, lng: lngParam },
    key: 'check-result',
    immediate: hasQueryCoords.value,
    server: hasQueryCoords.value,
    watch: [latParam, lngParam],
  },
)

watchEffect(() => {
  if (fetchError.value) {
    const e = fetchError.value as any
    error.value = e?.data?.statusMessage || e?.statusMessage || e?.message
      || t('check.error_generic')
  }
})

watchEffect(() => {
  if (!input.value && hasQueryCoords.value) {
    const lat = parseFloat(latParam.value)
    const lng = parseFloat(lngParam.value)
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      input.value = `${lat}, ${lng}`
    }
  }
})

const canonicalUrl = computed(() => {
  if (!result.value) return canonicalize(`${siteUrl}/check`)
  const lat = result.value.input.lat.toFixed(5)
  const lng = result.value.input.lng.toFixed(5)
  return canonicalize(`${siteUrl}/check?lat=${lat}&lng=${lng}`)
})

async function handleSubmit() {
  error.value = ''
  const parsed = parseCoordinates(input.value)
  if ('error' in parsed) {
    error.value = parsed.error === 'outside_iceland'
      ? t('check.error_outside_iceland')
      : t('check.error_unparseable')
    return
  }
  await router.push({
    path: '/check',
    query: { lat: parsed.lat.toFixed(5), lng: parsed.lng.toFixed(5) },
  })
}

const horizonProfileData = computed<HorizonProfileData | null>(() => {
  if (!result.value) return null
  const h = result.value.horizon
  if (h.coverage !== 'in-grid' || h.verdict === 'unknown' || h.clearanceDegrees == null) {
    return null
  }
  return {
    sun_azimuth: h.sunAzimuth,
    sun_altitude: h.sunAltitude,
    sweep: h.sweep,
    verdict: h.verdict as Exclude<typeof h.verdict, 'unknown'>,
    clearance_degrees: h.clearanceDegrees,
  }
})

useHead({
  title: () => result.value
    ? t('check.page_title_result', {
        lat: result.value.input.lat.toFixed(4),
        lng: Math.abs(result.value.input.lng).toFixed(4),
      })
    : t('check.page_title'),
  meta: [
    {
      name: 'description',
      content: () => {
        if (!result.value) return t('check.meta_description_empty')
        if (result.value.totality.insidePath) {
          return t('check.meta_description_in_path', {
            cloud: result.value.cloudHistory?.cell.avg_cloud_cover ?? '—',
          })
        }
        return t('check.meta_description_outside_path')
      },
    },
    { property: 'og:title', content: () => t('check.og_title') },
    { property: 'og:url', content: () => canonicalUrl.value },
    { property: 'og:type', content: 'website' },
  ],
  link: [
    { rel: 'canonical', href: canonicalize(`${siteUrl}/check`) },
  ],
})
</script>

<template>
  <PageShell screen="check">
    <div class="check-content">
    <header class="page-header">
      <Eyebrow tone="accent" class="page-eyebrow">{{ t('check.eyebrow') }}</Eyebrow>
      <h1 class="page-h1">{{ t('check.h1') }}</h1>
      <p class="page-subhead">{{ t('check.subhead') }}</p>
    </header>

    <!-- Input row: bare <div>, not <form>, so the browser cannot
         fall back to a native GET submit on hydration glitches. -->
    <div class="input-row">
      <div class="input-row-inner">
        <input
          v-model="input"
          type="text"
          autocomplete="off"
          spellcheck="false"
          :placeholder="t('check.input_placeholder')"
          class="coord-input"
          :aria-label="t('check.input_aria')"
          @keyup.enter="handleSubmit"
        >
        <button
          type="button"
          class="btn-corona coord-submit"
          :disabled="pending"
          @click="handleSubmit"
        >
          {{ pending ? t('check.submitting') : t('check.submit') }}
        </button>
      </div>
      <p v-if="error" class="input-error ec-banner-warn">{{ error }}</p>
    </div>

    <!-- Format hints — only on empty state. -->
    <section v-if="!result && !hasQueryCoords" class="hints">
      <Eyebrow tone="dim" class="hints-eyebrow">{{ t('check.hints_eyebrow') }}</Eyebrow>
      <ul class="hints-list">
        <li>{{ t('check.hint_decimal') }} <code>65.86182, -23.48019</code></li>
        <li>{{ t('check.hint_hemisphere') }} <code>65.86°N, 23.48°W</code></li>
        <li>{{ t('check.hint_google_maps') }} <code>https://www.google.com/maps/place/…/@65.86182,-23.48019,15z</code></li>
        <li>{{ t('check.hint_apple_maps') }} <code>https://maps.apple.com/?ll=65.86182,-23.48019</code></li>
      </ul>
      <p class="hints-tip">{{ t('check.hint_short_link_note') }}</p>
    </section>

    <!-- Loading -->
    <div v-if="pending && !result" class="loading-row">{{ t('check.loading') }}</div>

    <!-- Result -->
    <div v-if="result && !pending" class="result-stack">
      <CheckResultCard :result="result" />
      <CheckStatStrip :result="result" />

      <!-- Mini map — client-only (Mapbox doesn't SSR). Lazy import +
           skeleton placeholder so the page doesn't reflow on hydrate. -->
      <section class="result-section">
        <Eyebrow tone="dim">{{ t('check.section_location') }}</Eyebrow>
        <ClientOnly>
          <LazyCheckMiniMap :result="result" />
          <template #fallback>
            <div class="map-skeleton" aria-hidden="true" />
          </template>
        </ClientOnly>
      </section>

      <section v-if="horizonProfileData" class="result-section">
        <Eyebrow tone="dim">{{ t('check.section_horizon') }}</Eyebrow>
        <HorizonProfile
          :data="horizonProfileData"
          :lat="result.input.lat"
          :lng="result.input.lng"
        />
        <p
          v-if="result.horizon.nearestGridPoint && result.horizon.nearestGridPoint.distanceMeters > 50"
          class="snap-note"
        >
          {{ t('check.snap_note', {
            distance: Math.round(result.horizon.nearestGridPoint.distanceMeters),
            coords: formatLatLng(result.horizon.nearestGridPoint.lat, result.horizon.nearestGridPoint.lng),
          }) }}
        </p>
      </section>

      <section class="result-section">
        <Eyebrow tone="dim">{{ t('check.section_contact') }}</Eyebrow>
        <Card>
          <CheckContactTimes :result="result" />
        </Card>
      </section>

      <section v-if="result.cloudHistory" class="result-section">
        <CheckCloudHistory :result="result" />
      </section>

      <section class="result-section">
        <Eyebrow tone="dim">{{ t('check.section_share') }}</Eyebrow>
        <CheckShareButtons :result="result" />
      </section>

      <!-- Onward navigation into the archive. Informational only —
           nothing is for sale here any more. -->
      <section class="result-section">
        <Eyebrow tone="dim">{{ t('check.section_next') }}</Eyebrow>
        <div class="next-grid">
          <NuxtLinkLocale to="/spots" class="next-link">
            <Card>
              <div class="next-eyebrow">{{ t('check.cta_spots_eyebrow') }}</div>
              <div class="next-title">{{ t('check.cta_spots_title') }}</div>
              <div class="next-body">{{ t('check.cta_spots_body') }}</div>
              <div class="next-arrow" aria-hidden="true">→</div>
            </Card>
          </NuxtLinkLocale>
          <NuxtLinkLocale to="/guide" class="next-link">
            <Card>
              <div class="next-eyebrow">{{ t('check.cta_guide_eyebrow') }}</div>
              <div class="next-title">{{ t('check.cta_guide_title') }}</div>
              <div class="next-body">{{ t('check.cta_guide_body') }}</div>
              <div class="next-arrow" aria-hidden="true">→</div>
            </Card>
          </NuxtLinkLocale>
        </div>
      </section>
    </div>
    </div>
  </PageShell>
</template>

<style scoped>
/* Match the spots-list gutter pattern: PageShell already caps content
   at the default 768 px reading width; we just add 16 px horizontal
   padding on mobile and 24 px on tablet+. */
.check-content {
  width: 100%;
  padding-left: 16px;
  padding-right: 16px;
}
@media (min-width: 640px) {
  .check-content {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.page-header {
  margin-bottom: 24px;
}
.page-eyebrow {
  margin-bottom: 12px;
}
.page-h1 {
  font-family: 'Inter Tight', system-ui, sans-serif;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: rgb(var(--ink-1));
  line-height: 1.1;
  margin: 0 0 12px;
}
@media (min-width: 640px) {
  .page-h1 { font-size: 40px; }
}
@media (min-width: 1024px) {
  .page-h1 { font-size: 48px; }
}
.page-subhead {
  font-family: 'Inter Tight', system-ui, sans-serif;
  font-size: 15px;
  line-height: 1.55;
  color: rgb(var(--ink-1) / 0.78);
  max-width: 38rem;
}

.input-row {
  margin-bottom: 20px;
}
.input-row-inner {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
@media (min-width: 640px) {
  .input-row-inner { flex-direction: row; }
}
.coord-input {
  flex: 1;
  min-width: 0;
  padding: 13px 16px;
  background: rgb(var(--surface) / 0.04);
  border: 1px solid rgb(var(--border-subtle) / 0.18);
  border-radius: 8px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 13px;
  color: rgb(var(--ink-1));
  transition: border-color 0.15s, background 0.15s;
}
.coord-input::placeholder {
  color: rgb(var(--ink-1) / 0.32);
}
.coord-input:focus {
  outline: none;
  border-color: rgb(var(--accent) / 0.62);
  background: rgb(var(--surface) / 0.06);
}
.coord-submit {
  white-space: nowrap;
}
.coord-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.input-error {
  margin-top: 10px;
  padding: 10px 12px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11.5px;
  line-height: 1.5;
  letter-spacing: 0.02em;
  border-radius: 8px;
}

.hints {
  margin-top: 8px;
  margin-bottom: 28px;
}
.hints-eyebrow {
  margin-bottom: 10px;
}
.hints-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: 'Inter Tight', system-ui, sans-serif;
  font-size: 13.5px;
  color: rgb(var(--ink-1) / 0.78);
}
.hints-list code {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 12px;
  background: rgb(var(--surface) / 0.04);
  border: 1px solid rgb(var(--border-subtle) / 0.08);
  border-radius: 4px;
  padding: 1px 6px;
  margin-left: 6px;
  color: rgb(var(--ink-1));
  /* Long URL hints (Google / Apple Maps) can overflow the 720 px
     reading column on mobile — let them break at the natural / and ? */
  word-break: break-all;
  overflow-wrap: anywhere;
}
.hints-tip {
  margin-top: 12px;
  font-family: 'Inter Tight', system-ui, sans-serif;
  font-size: 12.5px;
  font-style: italic;
  color: rgb(var(--ink-1) / 0.62);
  line-height: 1.5;
}

.loading-row {
  padding: 48px 0;
  text-align: center;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgb(var(--ink-1) / 0.42);
}

.result-stack {
  display: flex;
  flex-direction: column;
  gap: 28px;
}
.result-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.snap-note {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgb(var(--ink-1) / 0.42);
}
/* Onward-navigation cards (end of a result page). Mirrors the card
   vocabulary used elsewhere on the page — shared Card primitive plus
   mono eyebrow / display title / dim body. */
.next-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
@media (min-width: 640px) {
  .next-grid { grid-template-columns: 1fr 1fr; }
}
.next-link {
  display: block;
  text-decoration: none;
  color: inherit;
  position: relative;
}
.next-link :deep(.ui-card) {
  position: relative;
  padding-right: 36px;
  transition: border-color 0.15s, background 0.15s;
}
.next-link:hover :deep(.ui-card) {
  border-color: rgb(var(--accent) / 0.32);
  background: rgb(var(--surface) / 0.07);
}
.next-eyebrow {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 9px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgb(var(--accent));
  margin-bottom: 6px;
}
.next-title {
  font-family: 'Inter Tight', system-ui, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: rgb(var(--ink-1));
  margin-bottom: 4px;
}
.next-body {
  font-family: 'Inter Tight', system-ui, sans-serif;
  font-size: 12.5px;
  line-height: 1.45;
  color: rgb(var(--ink-1) / 0.62);
}
.next-arrow {
  position: absolute;
  top: 14px;
  right: 14px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 14px;
  color: rgb(var(--ink-1) / 0.42);
  transition: color 0.15s, transform 0.15s;
}
.next-link:hover .next-arrow {
  color: rgb(var(--accent));
  transform: translateX(2px);
}

.map-skeleton {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  border: 1px solid rgb(var(--border-subtle) / 0.08);
  background:
    linear-gradient(180deg,
      rgb(var(--surface) / 0.06) 0%,
      rgb(var(--surface) / 0.02) 100%);
}
</style>
